import {Actions, createEffect, ofType} from '@ngrx/effects';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NGXLogger} from 'ngx-logger';
import {Injectable} from '@angular/core';
import {FirmenService} from '../../openapi/partner-openapi';
import {FirmaEntitiesActions} from '../actions/firma-entities.actions';
import {catchError, concatMap, delay, filter, map, retry, switchMap} from 'rxjs/operators';
import {of, take} from 'rxjs';
import {mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {Router} from '@angular/router';
import {SentryActions} from '../actions/sentry.actions';
import {FirmaEntitiesSelectors} from '../selectors/firma-entities.selectors';
import {Store} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {FirmaEditActions} from '../actions/firma-edit.actions';
import {BankkontoService as ZahlungBankkontoService} from '../../openapi/zahlung-openapi';
import {
  BankkontoDTO as FakturierungBankkontoDTO,
  BetriebService,
  EinstellungenService,
} from '../../openapi/fakturierung-openapi';


@Injectable()
export class FirmaEntitiesEffects {

  constructor(
    private actions$: Actions,
    private firmenService: FirmenService,
    private logger: NGXLogger,
    private snackbar: MatSnackBar,
    private router: Router,
    private store: Store<AppState>,
    private zahlungBankkontoService: ZahlungBankkontoService,
    private einstellungenService: EinstellungenService,
    private betriebService: BetriebService,
  ) {
  }

  /**
   * Effekt zum Laden eines Absenders anhand der Id, sofern es noch nicht im Store vorhanden ist.
   */
  readonly loadFirmaByIdIfAbsent$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.loadFirmaByIdIfAbsent),
      concatMap(action => this.store.select(FirmaEntitiesSelectors.firmaById(action.firmaId)).pipe(
        take(1),
        map(firmaDto => ({action, firmaDto}))
      )),
      filter(data => !data.firmaDto),
      map(data => FirmaEntitiesActions.readFirma({
        firmaId: data.action.firmaId,
      })),
    ),
  );

  /**
   * Lädt die Firma zur Id vom Service.
   */
  readonly readFirma$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.readFirma),
      switchMap(({
                   firmaId
                 }) => {
        return this.firmenService.readFirma(firmaId).pipe(
          map(firmaDto => {

            this.logger.debug(
              'read firma succeeded. firmaId: ',
              firmaId,
            );

            return FirmaEntitiesActions.readFirmaSuccess({
              firmaDto,
            });
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            concatMap(error => {
              this.logger.error(
                'read firma failed. error: ',
                error,
              );

              return [
                FirmaEntitiesActions.readFirmaFailure({
                  error: error,
                }),
                SentryActions.captureException({
                  error,
                  extras: {}
                }),
              ];
            }),
          ))
        );
      }),
    ),
  );

  /**
   * Error-Handling für das Laden einer Firma.
   */
  readonly readFirmaFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.readFirmaFailure),
      map(({
             error,
           }) => {

        let errorMsg = '';

        switch (error.status) {
          case 403: {
            errorMsg = 'Fehlende Berechtigungen für das Laden des Betriebs. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Betrieb nicht gefunden. Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 409: {
            // INFO: Bei fehlenden Rechten, wird der Anwender auf die Betrieb-Not-Found-Seite weitergeleitet.
            this.router.navigateByUrl('betrieb-not-found');
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden des Betriebs. Bitte probiere es später erneut.';
          }
        }

        if (errorMsg) {
          this.snackbar.open(
            errorMsg,
            undefined,
            {
              duration: 5000,
            });
        }
      }),
    ), {dispatch: false}
  );

  /**
   * Lädt alle Firmen des Akteurs vom Service.
   */
  readonly readFirmen$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.readFirmen),
      switchMap(() => {
        return this.firmenService.readFirmenExtendedList().pipe(
          map(firmenDtos => {

            this.logger.debug(
              'read firmen succeeded',
            );

            // INFO: Sollte die Liste leer sein, hat der Anwender nicht die nötigen Berechtigungen.
            if (firmenDtos.length === 0) {
              this.router.navigateByUrl('betrieb-not-found');
            }

            return FirmaEntitiesActions.readFirmenSuccess({
              firmenDtos,
            });
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'read firmen failed. error: ',
                error,
              );

              return FirmaEntitiesActions.readFirmenFailure({
                error: error,
              });
            }),
          ))
        );
      }),
    ),
  );

  /**
   * Error-Handling für das Laden von Firmen.
   */
  readonly readFirmenFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.readFirmenFailure),
      map(({
             error,
           }) => {

        let errorMsg = '';

        switch (error.status) {
          case 403: {
            errorMsg = 'Fehlende Berechtigungen für das Laden der Betriebe. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Keine Betriebe nicht gefunden. Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden der Betriebe. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
          });
      }),
    ), {dispatch: false}
  );

  /**
   * Aktualisieren einer Firma.
   */
  readonly updateFirma$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirma),
      switchMap(({
                   firmaId,
                   requestDto,
                 }) => {
        return this.firmenService.updateFirma(firmaId, requestDto).pipe(
          concatMap(() => {
            this.logger.debug(
              'update firma succeeded',
            );

            this.snackbar.open(
              'Betrieb erfolgreich aktualisiert.',
              undefined,
              {
                duration: 5000,
              });

            // INFO: Nach dem Aktualisieren wird der Anwender auf die Übersichtsseite weitergeleitet.
            this.router.navigateByUrl('overview');

            // INFO: Nach dem Aktualisieren wird die Firma neu geladen.
            return [
              FirmaEntitiesActions.readFirma({firmaId}),
              FirmaEntitiesActions.updateFirmaSuccess(),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            concatMap(error => {
              this.logger.error(
                'update firma failed. error: ',
                error,
                'firmaId: ',
                firmaId,
              );

              return [
                FirmaEntitiesActions.updateFirmaFailure({
                  error: error,
                }),
                SentryActions.captureException({
                  error,
                  extras: {}
                }),
              ];
            }),
          ))
        );
      }),
    ),
  );

  /**
   * Error-Handling für das Aktualisieren einer Firma.
   */
  readonly updateFirmaFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFailure),
      map(({
             error,
           }) => {

        let errorMsg = '';

        switch (error.status) {
          case 403: {
            errorMsg = 'Fehlende Berechtigungen für das Aktualisieren des Betriebs. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Benötigte Daten für das Aktualisieren des Betriebs nicht gefunden. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 409: {
            errorMsg = error.error + ' ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Aktualisieren des Betriebs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
          });
      }),
    ), {dispatch: false}
  );

  /**
   * Aktualisieren einer Firma im Rahmen der Fakturierungs-Integration.
   *
   * Schritt 1 / 5 - Aktualisieren der Stammdaten der Firma.
   */
  readonly updateFirmaFakturierungStep1UpdateStammdata$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep1UpdateStammdata),

      /*
       * INFO:
       * bankkontoUpdate wird als Action-Prop durch die Kette gereicht, nicht per withLatestFrom gelesen:
       * der Snapshot muss bei Schritt 1 gezogen werden, weil der 500-ms-Debounce des IBAN-Felds
       * den Store bis zu Schritt 3 noch verändern kann.
       */
      switchMap(({
                   firmaId,
                   firmaUpdateRequestDto,
                   bankkontoCreateRequestDto,
                   bankkontoUpdate,
                   einstellungenRequestDTO,
                 }) => {

        return this.firmenService.updateFirma(firmaId, firmaUpdateRequestDto).pipe(
          map(() => {
            this.logger.debug(
              'update firma data succeeded',
            );

            // INFO: Nach dem Aktualisieren findet das Handling für das Bankkonto statt.
            return FirmaEntitiesActions.updateFirmaFakturierungStep2HandleBankkonto({
              firmaId,
              bankkontoCreateRequestDto,
              bankkontoUpdate,
              einstellungenRequestDTO,
            });
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            concatMap(error => {
              this.logger.error(
                'update firma failed. error: ',
                error,
                'firmaId: ',
                firmaId,
              );

              return [
                FirmaEntitiesActions.updateFirmaFailure({
                  error: error,
                }),
                SentryActions.captureException({
                  error,
                  extras: {}
                }),
              ];
            }),
          ))
        );
      }),
    ),
  );

  /**
   * Aktualisieren einer Firma im Rahmen der Fakturierungs-Integration.
   *
   * Schritt 2 / 5 - Handling für das Ermitteln / Erstellen des Bankkontos.
   */
  readonly updateFirmaFakturierungStep2HandleBankkonto$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep2HandleBankkonto),
      switchMap(({
                   firmaId,
                   bankkontoCreateRequestDto,
                   bankkontoUpdate,
                   einstellungenRequestDTO,
                 }) => {
        if (bankkontoCreateRequestDto) {
          // INFO: Ist ein BankkontoRequestDTO vorhanden, so muss erst ein Bankkonto erstellt werden.

          return this.zahlungBankkontoService.createBankkonto(firmaId, bankkontoCreateRequestDto).pipe(
            switchMap(bankkontoDto => {
              this.logger.debug(
                'create bankkonto succeeded.',
              );

              /*
               * INFO:
               * Zahlung-Dto zu Fakturierung-Dto, daher das explizite Mapping. Bankbezeichnung und BIC
               * bewusst nicht aus dem Store, weil ein withLatestFrom die Daten einer verwaisten
               * readBankinformation-Antwort übernehmen könnte.
               */
              const createdBankkontoDto: FakturierungBankkontoDTO = {
                id: bankkontoDto.id,
                iban: bankkontoDto.iban || bankkontoCreateRequestDto.iban,
                bic: bankkontoDto.bic,
                bankbezeichnung: bankkontoDto.bankbezeichnung,
                kontoinhaber: bankkontoDto.kontoinhaber || bankkontoCreateRequestDto.kontoinhaber || '',
              };

              return [
                FirmaEditActions.readBankkonten({firmaId}),

                // INFO: Ohne Reset legt ein erneuter Speichern-Klick — etwa nach einem Fehler in einem
                //  der Folgeschritte — dasselbe Bankkonto ein zweites Mal an und bricht mit 409 ab.
                FirmaEditActions.updateBankkontoCreateRequestDto({
                  bankkontoCreateRequestDTO: undefined,
                }),

                // INFO: Bisher hält selectedBankkontoDto das synthetische Dto ohne Id — ohne Zurückschreiben
                //  liefe eine nachträgliche Änderung des Kontoinhabers ins Leere (stiller Datenverlust).
                FirmaEditActions.updateSelectedBankInformation({
                  bankkontoDto: createdBankkontoDto,
                }),

                // INFO: Sonst überschreibt ein zweiter Speichern-Klick die Einstellungen mit bankkontoId: undefined
                //  und hängt die frisch angelegte Bankverbindung wieder ab.
                FirmaEditActions.updateEinstellungenRequestDtoBankkontoId({
                  bankkontoId: bankkontoDto.id,
                }),

                FirmaEntitiesActions.updateFirmaFakturierungStep3UpdateBankkonto({
                  firmaId,

                  // INFO: Ein frisch angelegtes Bankkonto trägt den Kontoinhaber bereits aus dem Create-Dto.
                  bankkontoUpdate: undefined,
                  einstellungenRequestDTO: {
                    ...einstellungenRequestDTO,
                    bankkontoId: bankkontoDto.id,
                  },
                }),
              ];
            }),
            catchError(error => of(error).pipe(
              mappedHttpErrorResponseOperator(error),
              map(error => {
                this.logger.error(
                  'create bankkonto failed. error: ',
                  error,
                  'firmaId: ',
                  firmaId,
                );

                return FirmaEntitiesActions.updateFirmaFakturierungStep2Failure({
                  error: error,
                });
              }),
            )),
          );
        } else {
          // INFO: Ist kein BankkontoRequestDTO vorhanden, muss lediglich ein bestehendes Bankkonto aktualisiert werden.

          return of(FirmaEntitiesActions.updateFirmaFakturierungStep3UpdateBankkonto({
            firmaId,
            bankkontoUpdate,
            einstellungenRequestDTO,
          }));
        }
      }),
    ),
  );

  readonly createBankkontoFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep2Failure),
      map(({error}) => {

        let errorMsg = '';

        switch (error.status) {
          case 404: {
            errorMsg = 'Benötigte Daten für das Erstellen des Bankkontos nicht gefunden. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 409: {
            errorMsg = 'Das Bankkonto existiert bereits. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Das Bankkonto konnte nicht erstellt werden. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
          });
      }),
    ),
    {dispatch: false},
  );

  /**
   * Aktualisieren einer Firma im Rahmen der Fakturierungs-Integration.
   *
   * Schritt 3 / 5 - Aktualisieren des Kontoinhabers eines bereits existierenden Bankkontos.
   */
  readonly updateFirmaFakturierungStep3UpdateBankkonto$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep3UpdateBankkonto),
      switchMap(({
                   firmaId,
                   bankkontoUpdate,
                   einstellungenRequestDTO,
                 }) => {
        if (!bankkontoUpdate) {
          // INFO: Ohne Änderung am Kontoinhaber können die Konfigurationen direkt aktualisiert werden.

          return of(FirmaEntitiesActions.updateFirmaFakturierungStep4UpdateConfiguration({
            firmaId,
            einstellungenRequestDTO,
          }));
        }

        return this.zahlungBankkontoService.updateBankkonto(
          firmaId,
          bankkontoUpdate.bankkontoId,
          bankkontoUpdate.requestDto,
        ).pipe(
          switchMap(() => {
            this.logger.debug(
              'update bankkonto succeeded.',
            );

            return [
              FirmaEditActions.readBankkonten({firmaId}),
              FirmaEntitiesActions.updateFirmaFakturierungStep4UpdateConfiguration({
                firmaId,
                einstellungenRequestDTO,
              }),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'update bankkonto failed. error: ',
                error,
                'firmaId: ',
                firmaId,
                'bankkontoId: ',
                bankkontoUpdate.bankkontoId,
              );

              return FirmaEntitiesActions.updateFirmaFakturierungStep3Failure({
                error: error,
              });
            }),
          )),
        );
      }),
    ),
  );

  readonly updateFirmaFakturierungStep3Failure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep3Failure),
      map(({error}) => {

        let errorMsg = '';

        switch (error.status) {
          case 400: {
            errorMsg = 'Das Bankkonto konnte nicht aktualisiert werden. Bitte prüfe deine Eingabe.';
            break;
          }
          case 403: {
            errorMsg = 'Fehlende Berechtigungen für das Aktualisieren des Bankkontos. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Das zu aktualisierende Bankkonto wurde nicht gefunden. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Das Bankkonto konnte nicht aktualisiert werden. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
          });
      }),
    ),
    {dispatch: false},
  );

  /**
   * Schritt 4 / 5 - Aktualisieren der Konfiguration für die Einstellungen der Fakturierung.
   *
   * Aktualisiert das Einstellungen-Objekt einer Firma im Rahmen der Fakturierungs-Integration.
   */
  readonly updateFirmaFakturierungStep4UpdateConfiguration$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep4UpdateConfiguration),
      switchMap(({
                   firmaId,
                   einstellungenRequestDTO,
                 }) => {

        return this.einstellungenService.updateEinstellungenDefault(firmaId, einstellungenRequestDTO).pipe(
          delay(2000),
          retry({
            count: 3,
            delay: 1000,
            resetOnSuccess: false,
          }),
          map(() => {
            this.logger.debug(
              'update configuration succeeded',
            );

            return FirmaEntitiesActions.updateFirmaFakturierungStep5ReloadFakturierungBetrieb({
              firmaId,
            });
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'update configuration failed. error: ',
                error,
                'firmaId: ',
                firmaId,
              );

              return FirmaEntitiesActions.updateConfigurationFailure({
                error: error,
              });
            }),
          ))
        );
      }),
    ),
  );

  readonly updateConfigurationFailure = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateConfigurationFailure),
      map(({
             error,
           }) => {

        this.snackbar.open(
          'Fehler beim Aktualisieren des Betriebs. Bitte probiere es später erneut.',
          undefined,
          {
            duration: 5000,
          });
      }),
    ), {dispatch: false}
  );

  /**
   * Aktualisieren einer Firma im Rahmen der Fakturierungs-Integration.
   *
   * Schritt 5 / 5 - Nachladen der Firma in die Fakturierung-DB.
   *
   * Die Daten der Firma werden erst von der ADNOVA+ Oracle-DB in die Fakturierung-DB übertragen,
   * wenn das Laden dieser angestoßen wird. Aus diesem Grund muss dies,
   * im Rahmen des Speicher-Vorgangs angestoßen werden.
   */
  readonly updateFirmaFakturierungStep5ReloadFakturierungBetrieb$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep5ReloadFakturierungBetrieb),
      switchMap(({
                   firmaId,
                 }) => {

        return this.betriebService.getBetrieb(firmaId).pipe(
          map(() => {
            this.logger.debug(
              'update betrieb at fakturierung-db succeeded',
            );

            return FirmaEntitiesActions.updateFirmaSuccess();
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'update betrieb at fakturierung-db failed. error: ',
                error,
                'firmaId: ',
                firmaId,
              );

              return FirmaEntitiesActions.updateFirmaFakturierungStep5Failure({
                error: error,
              });
            }),
          ))
        );
      }),
    ),
  );

  readonly updateFirmaFakturierungStep5Failure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FirmaEntitiesActions.updateFirmaFakturierungStep5Failure),
      map(({error}) => {

        let errorMsg = '';

        switch (error.status) {
          case 403: {
            errorMsg = 'Fehlende Berechtigungen zum Aktualisieren der Firma. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Die zu aktualisierende Firma konnte nicht gefunden werden. ' +
              'Bitte Kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Aktualisieren des Betriebs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
          });
      }),
    ),
    {dispatch: false},
  );

}
